// Generates MCP tool JSON Schema from the shared Data API descriptors so the
// advertised filter grammar always matches the API. The shapes mirror the
// API's FieldFilter / RangeFilter (apps/api grammar.ts) and its .partial()
// .strict() semantics: every key optional, only FieldFilter.value required,
// additionalProperties false at every level.
import type { FieldDescriptor, SearchSchemaDescriptor } from '@parcel/types';

type JsonSchema = Record<string, unknown>;

// Schema budget levers. Registry prose is clipped, not deleted: full
// descriptions and enum vocabularies live in the fields dataset
// (search_fields / get_fields); schemas keep structure plus a hint.
export const TOOL_SCHEMA_BUDGET = 10240;
export const TOP_LEVEL_DESCRIPTION_MAX = 80;
export const NESTED_HINT_MAX = 40;

function clip(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max)}...`;
}

// Leaf props carry no per-instance descriptions: the shapes ({value,
// exact_match?, exclude?} and {min, max}) are documented once in the filters
// block description, and repeating the prose per field is what pushed the big
// tools over the client's schema cap. Every key and type stays.
export const FIELD_FILTER_SCHEMA: JsonSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    value: {
      oneOf: [
        { type: 'string' },
        { type: 'number' },
        { type: 'array', items: { type: ['string', 'number'] }, minItems: 1 },
      ],
    },
    exact_match: { type: 'boolean' },
    exclude: { type: 'boolean' },
  },
  required: ['value'],
};

// Kind-aware value shapes: a text field's value is a string or strings (the
// generic number branch is meaningless there and costs bytes on every field),
// and exact_match only applies to text. Number/date kinds keep the generic
// shape via FIELD_FILTER_SCHEMA.
const TEXT_FILTER_SCHEMA: JsonSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    value: {
      oneOf: [{ type: 'string' }, { type: 'array', items: { type: 'string' }, minItems: 1 }],
    },
    exact_match: { type: 'boolean' },
    exclude: { type: 'boolean' },
  },
  required: ['value'],
};

export const RANGE_FILTER_SCHEMA: JsonSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {
    min: { oneOf: [{ type: 'number' }, { type: 'string', format: 'date' }] },
    max: { oneOf: [{ type: 'number' }, { type: 'string', format: 'date' }] },
  },
};

function enumFilterSchema(field: FieldDescriptor, datasetId: string, nested: boolean): JsonSchema {
  const description = nested
    ? clip(field.description, NESTED_HINT_MAX)
    : `${clip(field.description, TOP_LEVEL_DESCRIPTION_MAX)} Values: get_fields({dataset:"${datasetId}", keys:["${field.key}"]}).`;
  return {
    type: 'object',
    additionalProperties: false,
    required: ['value'],
    description,
    properties: {
      value: {
        oneOf: [{ type: 'string' }, { type: 'array', items: { type: 'string' }, minItems: 1 }],
      },
      exclude: { type: 'boolean' },
    },
  };
}

function fieldFilterSchema(field: FieldDescriptor, datasetId: string, nested: boolean): JsonSchema {
  if (field.kind === 'enum') return enumFilterSchema(field, datasetId, nested);
  const max = nested ? NESTED_HINT_MAX : TOP_LEVEL_DESCRIPTION_MAX;
  const base = field.kind === 'text' ? TEXT_FILTER_SCHEMA : FIELD_FILTER_SCHEMA;
  return { ...base, description: clip(field.description, max) };
}

// A number field's bounds are numbers and a date field's are dates; the other branch never validates.
const NUMBER_RANGE_SCHEMA: JsonSchema = {
  ...RANGE_FILTER_SCHEMA,
  properties: { min: { type: 'number' }, max: { type: 'number' } },
};
const DATE_RANGE_SCHEMA: JsonSchema = {
  ...RANGE_FILTER_SCHEMA,
  properties: {
    min: { type: 'string', format: 'date' },
    max: { type: 'string', format: 'date' },
  },
};

function rangeFieldSchema(field: FieldDescriptor, nested: boolean): JsonSchema {
  const max = nested ? NESTED_HINT_MAX : TOP_LEVEL_DESCRIPTION_MAX;
  const shape = field.kind === 'date' ? DATE_RANGE_SCHEMA : NUMBER_RANGE_SCHEMA;
  return { ...shape, description: clip(field.description, max) };
}

export function buildFilterSchema(
  descriptor: SearchSchemaDescriptor,
  datasetId: string,
): JsonSchema {
  const properties: Record<string, JsonSchema> = {};
  const advertised = (f: FieldDescriptor) => !f.omitFromMcp;
  for (const f of descriptor.filterFields.filter(advertised))
    properties[f.key] = fieldFilterSchema(f, datasetId, false);
  for (const f of descriptor.rangeFields.filter(advertised))
    properties[f.key] = rangeFieldSchema(f, false);
  for (const [relKey, inner] of Object.entries(descriptor.relational ?? {})) {
    const innerProps: Record<string, JsonSchema> = {};
    for (const f of inner.filterFields) innerProps[f.key] = fieldFilterSchema(f, datasetId, true);
    for (const f of inner.rangeFields) innerProps[f.key] = rangeFieldSchema(f, true);
    properties[relKey] = {
      type: 'object',
      additionalProperties: false,
      description: clip(inner.description, TOP_LEVEL_DESCRIPTION_MAX),
      properties: innerProps,
    };
  }
  return { type: 'object', additionalProperties: false, properties };
}

export function buildSearchInputSchema(
  descriptor: SearchSchemaDescriptor,
  datasetId: string,
): JsonSchema {
  return {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        minLength: 1,
        description: descriptor.queryDescription ?? 'Full-text search query.',
      },
      filters: {
        ...buildFilterSchema(descriptor, datasetId),
        description: `Filter criteria. Direct fields take {value, exact_match?, exclude?}: value is one or an array (matches any), exact_match forces exact case-insensitive text match, exclude negates. Range fields take {min, max}, inclusive, numbers or YYYY-MM-DD. Relational keys filter by a related entity. Discover fields and enum values: search_fields({dataset:"${datasetId}"}).`,
      },
      sort: {
        type: 'object',
        additionalProperties: false,
        description:
          'Sort order. field must be one of the listed sort keys; order defaults to asc.',
        properties: {
          field: { enum: [...descriptor.sortKeys] },
          order: { enum: ['asc', 'desc'], default: 'asc' },
        },
        required: ['field'],
      },
      limit: {
        type: 'number',
        minimum: 1,
        maximum: 100,
        default: 25,
        description: 'Max results (1 to 100).',
      },
      offset: {
        type: 'number',
        minimum: 0,
        maximum: 10000,
        default: 0,
        description: 'Number of results to skip.',
      },
      search_after: { type: 'string', description: 'Cursor token for paginating beyond offset.' },
    },
  };
}

export function buildGetInputSchema(includeKeys: string[]): JsonSchema {
  return {
    type: 'object',
    properties: {
      ids: {
        type: 'array',
        items: { type: 'string' },
        minItems: 1,
        description: 'Entity IDs to fetch.',
      },
      include: {
        type: 'array',
        items: { enum: [...includeKeys] },
        description: `Optional related resources to include: ${includeKeys.join(', ')}.`,
      },
    },
    required: ['ids'],
  };
}
